import { motion } from "framer-motion";
import { Copy, Check } from "lucide-react";
import { useState } from "react";
import { useLang } from "@/i18n";

export function GoldMark({ size = 34 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id="gm-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F5D778" />
          <stop offset="0.55" stopColor="#D4AF37" />
          <stop offset="1" stopColor="#8a6d1f" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="30" fill="#050807" />
      <circle cx="32" cy="32" r="28.5" fill="none" stroke="url(#gm-g)" strokeWidth="3" />
      <circle cx="32" cy="32" r="22" fill="none" stroke="#009B3A" strokeWidth="1.4" opacity="0.85" />
      <text x="32" y="41" textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontSize="24" fontWeight="900" fill="url(#gm-g)">M</text>
    </svg>
  );
}

export function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function SectionHead({ eyebrow, title, sub, align = "left" }) {
  return (
    <Reveal className={align === "center" ? "text-center" : "text-left"}>
      <p className="eyebrow" data-testid="section-eyebrow">{eyebrow}</p>
      <h2 className="mt-4 max-w-4xl font-display text-4xl uppercase leading-[0.95] tracking-tight text-paper sm:text-5xl lg:text-6xl" data-testid="section-title">
        {title}
      </h2>
      {sub && <p className={`mt-5 max-w-2xl text-sm leading-relaxed text-mist sm:text-base ${align === "center" ? "mx-auto" : ""}`}>{sub}</p>}
    </Reveal>
  );
}

export function PageHero({ eyebrow, title, sub }) {
  return (
    <div className="section-pad relative overflow-hidden pb-14 pt-36 sm:pt-40">
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 h-96 w-96 rounded-full bg-gold/10 blur-[120px]" />
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 top-24 h-80 w-80 rounded-full bg-brgreen/15 blur-[110px]" />
      <motion.p
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="eyebrow" data-testid="page-eyebrow"
      >
        {eyebrow}
      </motion.p>
      <div className="overflow-hidden">
        <motion.h1
          initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
          className="mt-4 font-display text-5xl uppercase leading-[0.95] tracking-tight text-paper sm:text-6xl lg:text-7xl"
          data-testid="page-title"
        >
          {title}
        </motion.h1>
      </div>
      {sub && (
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-6 max-w-2xl text-sm leading-relaxed text-mist sm:text-base"
        >
          {sub}
        </motion.p>
      )}
    </div>
  );
}

const STATUS_STYLES = {
  coming_soon: "border-gold/40 bg-gold/10 text-goldlight",
  available: "border-neon/40 bg-neon/10 text-neon",
  minted: "border-brgreen/50 bg-brgreen/15 text-neon",
  sold_out: "border-white/15 bg-white/5 text-mist",
  planned: "border-white/15 bg-white/5 text-mist",
  in_progress: "border-neon/40 bg-neon/10 text-neon",
  completed: "border-brgreen/50 bg-brgreen/15 text-neon",
  delayed: "border-red-400/40 bg-red-400/10 text-red-300",
};

export function StatusChip({ status, label }) {
  return (
    <span className={`mono-chip border ${STATUS_STYLES[status] || STATUS_STYLES.planned}`} data-testid={`status-${status}`}>
      {label}
    </span>
  );
}

export function CopyButton({ value }) {
  const { t } = useLang();
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid="copy-btn"
      className="mono-chip border-gold/40 text-goldlight transition-colors hover:border-goldlight hover:text-paper"
    >
      {done ? <Check size={12} /> : <Copy size={12} />}
      {done ? t("common.copied") : t("common.copy")}
    </button>
  );
}

export function GoldParticles({ count = 18 }) {
  const dots = Array.from({ length: count }, (_, i) => ({
    left: `${(i * 53) % 100}%`,
    top: `${(i * 37) % 100}%`,
    size: 2 + ((i * 7) % 4),
    delay: (i % 9) * 0.7,
    dur: 6 + (i % 5) * 2,
  }));
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((d, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            left: d.left, top: d.top, width: d.size, height: d.size,
            background: i % 3 === 0 ? "#A8FF3E" : "#D4AF37",
            boxShadow: `0 0 ${d.size * 3}px ${i % 3 === 0 ? "rgba(168,255,62,.7)" : "rgba(212,175,55,.7)"}`,
          }}
          animate={{ y: [0, -26, 0], opacity: [0.15, 0.8, 0.15] }}
          transition={{ duration: d.dur, delay: d.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}
